import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Match Invoice Line Items Step
 *
 * Performs 2-way matching between invoice line items and PO line items.
 *
 * Uses:
 * - Embedding-based description matching
 * - Quantity and price comparison
 * - AI arbitration for ambiguous matches
 * - Stores entity associations for matched items
 *
 * Note: This is a backend-only step that requires:
 * - AI model access for arbitration
 * - Database write access for EntityAssociations
 */
export const matchInvoiceLineItemsStep = defineStepConfig({
  type: "match_invoice_line_items",
  name: "Match Invoice Line Items",
  description:
    "Match invoice line items to purchase order line items with AI-enhanced confidence scoring",
  category: "Transformation",
  libraryModule: "AccountsPayable",
  inputSchema: z.object({
    apInvoice: entityOfType(SystemEntity.AP_INVOICE).describe(
      "AP Invoice to reconcile",
    ),
    /** @deprecated Use purchaseOrders instead for multiple PO support */
    purchaseOrder: entityOfType(SystemEntity.INTERNAL_PURCHASE_ORDER)
      .describe(
        "@deprecated Use purchaseOrders instead. Single Internal Purchase Order to match against.",
      )
      .optional(),
    purchaseOrders: z
      .array(entityOfType(SystemEntity.INTERNAL_PURCHASE_ORDER))
      .optional()
      .describe("Array of Internal Purchase Orders to match against"),
    salesOrders: z
      .array(entityOfType(SystemEntity.AR_SALES_ORDER))
      .optional()
      .describe("Optional array of Sales Orders to match against"),
    transferOrders: z
      .array(entityOfType(SystemEntity.IM_TRANSFER_ORDER))
      .optional()
      .describe("Optional array of Transfer Orders to match against"),
  }),

  outputSchema: z.object({
    lowestConfidence: z
      .number()
      .min(0)
      .max(1)
      .describe("Lowest confidence score among all line item matches"),
    // TODO(GEN-1861): Temporary order-status gate output. Remove when proper sync-gating lands.
    // https://linear.app/gently-ai/issue/GEN-1861/cleanup-remove-hack-in-matchinvoicelineitemshandler
    matchedOrderNotReady: z
      .boolean()
      .describe(
        "True when a matched order is not yet in a status that allows matching in the source ERP (e.g. its fields are still being entered). Deterministic routing flag for defer-and-rerun.",
      ),
    unreadyReferences: z
      .array(
        z.object({
          type: z.enum(["SO", "PO"]),
          id: z.string(),
          currentStatus: z.string().nullable(),
        }),
      )
      .describe(
        "Matched orders whose status is not yet ready for matching. Empty when matchedOrderNotReady is false.",
      ),
    rerunLimitReached: z
      .boolean()
      .describe(
        "True when the agent wanted to defer for unready upstream data but the rerun limit refused it. No rerun is scheduled, so route this to human review — deferring again would wait forever.",
      ),
  }),

  configSchema: z.object({
    matchConfidenceThreshold: z
      .number()
      .min(0)
      .max(1)
      .prefault(0.7)
      .describe("Minimum confidence to auto-accept a match"),
    enableFuzzySearch: z
      .boolean()
      .prefault(true)
      .describe("Enable AI-powered fuzzy matching when no references"),
    agentFallbackConfidenceThreshold: z
      .number()
      .min(0)
      .max(1)
      .prefault(0.7)
      .describe(
        "When the reconciliation easy-path produces a lowest confidence below this threshold, fall back to the AI matching agent",
      ),
    priceTolerancePercent: z
      .number()
      .prefault(1)
      .describe("Price variance tolerance for matching (percentage)"),
    quantityTolerancePercent: z
      .number()
      .prefault(5)
      .describe("Quantity variance tolerance for matching (percentage)"),
    requiredInvoiceFields: z
      .object({
        supplier: z
          .boolean()
          .prefault(false)
          .describe("Require the invoice to have a supplier before matching"),
        referenceIds: z
          .boolean()
          .prefault(false)
          .describe(
            "Require the invoice to have at least one reference ID before matching",
          ),
        totalAmount: z
          .boolean()
          .prefault(false)
          .describe(
            "Require the invoice to have a total amount before matching",
          ),
      })
      .prefault({})
      .describe(
        "Optional invoice prerequisites that must be present before line-item matching runs",
      ),
    agentSnooze: z
      .object({
        delayMinutes: z
          .number()
          .int()
          .min(1)
          .max(10080)
          .prefault(60)
          .describe(
            "How long to defer the workflow when the agent snoozes for unready upstream data",
          ),
        maxReruns: z
          .number()
          .int()
          .min(1)
          .max(1000)
          .optional()
          .describe(
            "Stop rerunning after this many agent-initiated snoozes. Unlimited when omitted.",
          ),
        expiresAfterMinutes: z
          .number()
          .int()
          .min(1)
          .optional()
          .describe(
            "Stop rerunning once this much time has elapsed since the original run. Unlimited when omitted.",
          ),
      })
      .prefault({})
      .describe(
        "Bounds on the snoozeWorkflowRerun tool when the matching agent defers because an upstream order isn't ready",
      ),
  }),

  emits: [] as const,
});
